import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/dashboard.service';
import { handleApiCall, displayError, ApiError } from '../lib/error-handler';
import { authService } from '../services/auth.service';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Alert, AlertDescription } from '../components/ui/Alert';
import { PromptEditor } from '../components/dashboard/PromptEditor';
import { FeedManager } from '../components/dashboard/FeedManager';
import { JobRunner } from '../components/dashboard/JobRunner';
import { RecentJobs } from '../components/dashboard/RecentJobs';
import type { Prompt, RSSFeed, Job } from '../types/api';

export function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [feeds, setFeeds] = useState<RSSFeed[]>([]);
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [runningJob, setRunningJob] = useState<Job | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load prompt, feeds, and recent jobs in parallel
      const [promptData, feedsData, jobsData] = await Promise.all([
        handleApiCall(
          () => dashboardService.getOrCreateDefaultPrompt(),
          'Failed to load prompt'
        ),
        handleApiCall(
          () => dashboardService.getFeeds(),
          'Failed to load feeds'
        ),
        handleApiCall(
          () => dashboardService.listJobs(undefined, 10, 0),
          'Failed to load recent jobs'
        )
      ]);

      setPrompt(promptData);
      setFeeds(feedsData);
      setRecentJobs(jobsData.jobs);
    } catch (err) {
      // Check if it's an auth error
      if (err instanceof ApiError && err.isAuthError) {
        // Clear tokens and redirect to login
        authService.clearAuth();
        navigate('/login');
        return;
      }
      setError(displayError(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePromptSave = async (promptText: string, name?: string) => {
    if (!prompt) return;

    try {
      const updated = await handleApiCall(
        () => dashboardService.updatePrompt(prompt.id, {
          prompt_text: promptText,
          ...(name && { name })
        }),
        'Failed to save prompt'
      );
      setPrompt(updated);
    } catch (err) {
      throw err; // Let the component handle the error
    }
  };

  const handleFeedsChange = (updatedFeeds: RSSFeed[]) => {
    setFeeds(updatedFeeds);
  };

  const handleJobStart = (job: Job) => {
    setRunningJob(job);
  };

  const handleJobComplete = (jobId: string) => {
    setRunningJob(null);
    // Refresh recent jobs
    loadDashboardData();
    // Navigate to results
    navigate(`/results/${jobId}`);
  };

  const handleJobError = () => {
    setRunningJob(null);
    loadDashboardData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-muted-foreground">
          Manage your analysis criteria, RSS feeds, and run analysis jobs
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Prompt Editor */}
          {prompt && (
            <PromptEditor
              prompt={prompt}
              onSave={handlePromptSave}
              disabled={!!runningJob}
            />
          )}

          {/* Feed Manager */}
          <FeedManager
            feeds={feeds}
            onChange={handleFeedsChange}
            disabled={!!runningJob}
          />
        </div>

        {/* Sidebar - Right column */}
        <div className="space-y-6">
          {/* Job Runner */}
          <JobRunner
            onJobStart={handleJobStart}
            onJobComplete={handleJobComplete}
            onJobError={handleJobError}
            currentJob={runningJob}
            disabled={feeds.filter(f => f.is_active).length === 0}
          />

          {/* Recent Jobs */}
          <RecentJobs jobs={recentJobs} />
        </div>
      </div>
    </div>
  );
}
